import type { SignalColor, SignalItem, SignalSlot } from '@indipia/shared'
import type { SignalAction } from '../constants/signalView'

// 리스트 보기(sched-list) 공용 타입 — 단계·행 모델·집계·필터·정렬 (구현계획 B-00 · L1_C6_신규사양).
//
// 사용 지침
//  - 이 파일은 타입과 값이 고정된 상수만 둔다. 판정·계산 함수는 두지 않는다.
//      constants/scheduleListStage.logic.ts = B-01~04 (3칸 단계 판정·펼침 줄 단계 문구)
//      constants/scheduleList.logic.ts      = B-05~12 (대표 원장 행·행 모델·요약·필터·정렬·다음 할 일·마일스톤·기간)
//      hooks/useScheduleListState.ts·useScheduleListData.ts·useScheduleListActions.ts·useScheduleListRemark.ts = C-01~04
//      components/list/* = D 작업. 화면은 이 타입의 값을 그대로 그린다(판정을 다시 하지 않는다).
//  - 신호 색·사유는 서버 값이다(SignalItem.colorCode·reasonCode). 화면·로직이 날짜로 다시 계산하거나 색을 덮어쓰지 않는다.
//    단계 위치(d·c)만 reasonCode 로 정한다. 색이 흰색으로 고정되는 칸(미배정·거래처 미배정·발주일 도래전·작업요청전)은
//    StageState.color 에 WHITE 를 담아 화면이 서버 색을 쓰지 않게 한다.
//  - 필수 값이 없으면 기본값·시스템값으로 채우지 않는다. 신호가 없으면 StageCell 'no-signal',
//    모르는 reasonCode 는 'unknown' — 화면은 각각 '신호 없음'·'표시할 수 없는 신호 상태'를 보여 준다.
//  - 날짜는 'YYYY-MM-DD' 문자열(로컬 달력일), 오늘 값은 호출 쪽이 isoDay(new Date()) 로 넘긴다. 로직 안에서 new Date() 를 직접 쓰지 않는다.
//  - 단계 인덱스는 0부터. d = 끝난 단계 수(i < d 이면 '끝남'), c = 지금 단계 인덱스(없으면 null, i === c 이면 '지금').
//    신본 코드의 c = -1 은 null 로 옮긴다.
//  - 식별은 숫자 PK(taskId·orderVendorId)만 쓴다. 공종명으로 찾지 않는다(딥링크·선행 목록 포함).

// ── 3칸 단계 ───────────────────────────────────────────────────────────

/** 3칸 종류 = 신호 슬롯(반장 배정 · 발주·입고 · 작업요청·검수) */
export type StageKind = SignalSlot

/** 칸별 단계 이름(신본 이름, L1 §2). 칸 수 = 배열 길이(반장 4 · 발주 6 · 작업 3) */
export const STAGE_STEP_NAMES: Record<StageKind, readonly string[]> = {
  FOREMAN: ['시공파트너 배정', '일정확인 요청', '반장님 회신', '일정 확정'],
  ORDER: ['자재파트너 배정', '발주정보 요청', '거래처 회신', '배송정보 요청', '배송정보 회신', '입고'],
  WORK: ['작업요청', '작업보고', '검수완료'],
}

/** 칸 제목(표 머리글·펼침 줄 이름) */
export const STAGE_KIND_LABEL: Record<StageKind, string> = {
  FOREMAN: '반장 배정',
  ORDER: '발주 · 입고',
  WORK: '작업요청 · 검수',
}

/** 단계 막대 한 칸의 상태 */
export type StageStepState = 'done' | 'current' | 'pending'

/** 3칸 중 한 칸의 판정 결과(B-01~03 반환 값) */
export interface StageState {
  kind: StageKind
  /** 끝난 단계 수 0~stepCount */
  d: number
  /** 지금 단계 인덱스. 지금 단계가 없으면 null */
  c: number | null
  /** 표시 색. 서버 색을 그대로 쓰는 칸은 서버 값, 흰색·녹색·파랑 고정 칸은 그 값. 해당 없음(NONE)은 이 값으로 오지 않는다 */
  color: SignalColor
  /** 상태 글자(signalPresentation(...).name). 거래처 없음은 '거래처 미배정' */
  label: string
  /** 단계 막대 칸 수 = STAGE_STEP_NAMES[kind].length */
  stepCount: number
  /** 이 칸을 정한 서버 사유 코드(툴팁·펼침 사유 조회용). 거래처 미배정은 원래 사유 코드가 그대로 남음 */
  reasonCode: string
  /** 발주 칸 전용: 거래처 없음으로 '거래처 미배정'이 된 칸. 다른 칸은 false */
  vendorMissing: boolean
}

/**
 * 칸 1개의 표시 모델 — 화면은 status 로만 분기한다.
 *  stage    = 막대 표시, none = 해당 없음(발주 ORDER_NA = '직접 발주 없음', 시공 FOREMAN_NA = 자재파트너 시공 · 막대 없음, 요약 분모 제외),
 *  no-signal= 이 공종의 신호가 없음('신호 없음'), unknown = 모르는 reasonCode('표시할 수 없는 신호 상태').
 */
export type StageCell =
  | { status: 'stage'; stage: StageState }
  | { status: 'none'; /** 시공 칸의 해당 없음(FOREMAN_NA)이면 'FOREMAN', 스케줄 전용 행정 공정 칸이면 'SCHEDULE_ONLY'. 발주 해당 없음은 생략 */ slot?: 'FOREMAN' | 'SCHEDULE_ONLY' }
  | { status: 'no-signal' }
  | { status: 'unknown'; reasonCode: string }

/** 칸 3개(반장·발주·작업). 발주 칸 조회 실패 시 order 는 'order-error' — 발주 칸만 부분 오류로 그린다(C-01, '거래처 미배정'으로 숨기지 않음) */
export interface ScheduleListCells {
  foreman: StageCell
  order: StageCell | { status: 'order-error' }
  work: StageCell
}

// ── 다음 할 일 · 기간 ───────────────────────────────────────────────────

/** 다음 할 일(B-10). 빨강·노랑이고 처리 버튼이 있는 신호 중 하나 */
export interface NextAction {
  slot: SignalSlot
  /** 빨강이면 채움 버튼, 노랑이면 보조 버튼 */
  color: 'RED' | 'YELLOW'
  reasonCode: string
  /** 서버가 내려준 처리 버튼(문구·동작 유형·주소) — 화면이 행선지를 고르지 않는다 */
  action: SignalAction
  /** 버튼 문구(서버 actionName) */
  buttonLabel: string
}

/** 기간 칸 상태 줄 종류 */
export type PeriodState = 'ended' | 'running' | 'upcoming'

/** 기간 칸 문구(B-12) */
export interface PeriodText {
  /** 'M/D ~ M/D' */
  range: string
  /** 상태 줄: '끝남' · '진행중 (경과)/n일' · 'n일 · 착공 D-n' 형식(일수 n 기준은 계획서 6-6) */
  status: string
  state: PeriodState
  /** 일수 n */
  days: number
}

// ── 행 모델 ────────────────────────────────────────────────────────────

/** 선행 공종 한 건 */
export interface ListPredecessor {
  taskId: number
  workTypeName: string
}

/** 리스트 행 1건 = 공종 1건(taskId 기준). 화면은 이 값만 읽는다 */
export interface ScheduleListRow {
  taskId: number
  workTypeCode: string
  workTypeName: string
  /** 'YYYY-MM-DD' */
  startDate: string
  endDate: string
  /** 일수(착공~종료 달력일, 계획서 6-6 기준에 따라 B-12가 정함) */
  duration: number
  /** 선행 공종 목록. 비어 있으면 '선행 없음' */
  predecessors: ListPredecessor[]
  /** 신호 3줄(슬롯별 서버 원본). 없으면 undefined */
  signals: Record<SignalSlot, SignalItem | undefined>
  /** 이 공종의 신호가 없음(신호 응답에 taskId 없음) — 점·칸에 '신호 없음' */
  signalMissing: boolean
  /** 행 대표색 = 서버 representativeColorCode. null(모두 흰색·신호 없음)이면 WHITE. '셋 다 흰색 숨김'은 리스트에 적용하지 않음 */
  representativeColor: SignalColor
  cells: ScheduleListCells
  /** 다음 할 일. 없으면 null('—') */
  nextAction: NextAction | null
  period: PeriodText
  /** 상대방 이름: 반장(없으면 null → '반장 미배정') · 거래처(없으면 null → '거래처 미배정') */
  foremanName: string | null
  vendorName: string | null
  /** 이 공종에 원장 거래처가 있음. 발주 해당 없음이면 의미 없음(false) */
  hasVendor: boolean
  /** 스케줄 전용 행정 공정(공종 마스터 scheduleOnlyYn='Y') — 시공·자재가 없어 신호 점·3칸·다음 할 일·펼침 상세가 없다 */
  scheduleOnly: boolean
}

/** 마일스톤 행(B-11) */
export interface ListMilestoneRow {
  milestoneId: number
  milestoneType: string | null
  milestoneName: string
  /** 'YYYY-MM-DD' */
  date: string
  /** 오늘 기준 남은 일수. 양수 = D-n, 0 = D-day, 음수 = D+n */
  dday: number
  /** '◆ 이름 · M/D(요일) · D-n' 중 'D-n' 부분 */
  ddayText: string
}

/** 표에 그릴 한 줄 — 공종 행 또는 마일스톤 행(mergeMilestones 반환) */
export type ListDisplayItem =
  | { kind: 'task'; row: ScheduleListRow }
  | { kind: 'milestone'; milestone: ListMilestoneRow }

// ── 요약 ───────────────────────────────────────────────────────────────

/** 진행 막대 한 개 */
export interface ListProgressStat {
  /** 분자 */
  done: number
  /** 분모 */
  total: number
  /** round(done/total×100), total 이 0이면 0 */
  percent: number
}

/** 요약줄(B-07). 필터와 무관, 현장 전체 공종 기준 */
export interface ListSummary {
  /** 반장 일정 확정 = FOREMAN_CONFIRMED / 전체 공종 */
  foreman: ListProgressStat
  /** 발주 · 입고 완료 = ORDER_RECEIVED / 발주 해당 없음이 아닌 공종('거래처 미배정' 포함) */
  order: ListProgressStat
  /** 작업 검수완료 = WORK_DONE / 전체 공종 */
  work: ListProgressStat
  /** 조치 필요 = 대표색 빨강 공종 수 */
  redCount: number
  /** 대기 = 대표색 노랑 공종 수 */
  yellowCount: number
  /** 총 n개 공종 */
  taskCount: number
  /** 마일스톤 m개 */
  milestoneCount: number
}

// ── 필터 · 정렬 ─────────────────────────────────────────────────────────

/**
 * 필터 값. 한 번에 하나, 같은 칩을 다시 눌러도 해제되지 않음.
 *  ''=전체 · ry=빨강·노랑만 · p=반장 미확정 · o=발주·입고 미완 · w=작업·검수 미완 · wk=이번주 착공
 */
export type ListFilter = '' | 'ry' | 'p' | 'o' | 'w' | 'wk'

/** 필터 칩 순서·문구(L1 §4-1, 칩 문구는 간트 칩과 맞춰 '빨강·노랑만') */
export const LIST_FILTER_OPTIONS: readonly { value: ListFilter; label: string }[] = [
  { value: '', label: '전체' },
  { value: 'ry', label: '빨강·노랑만' },
  { value: 'p', label: '반장 미확정' },
  { value: 'o', label: '발주·입고 미완' },
  { value: 'w', label: '작업·검수 미완' },
  { value: 'wk', label: '이번주 착공' },
]

/** 필터 칩별 건수(현재 행 전체 기준, 다른 필터와 무관) */
export type ListFilterCounts = Record<ListFilter, number>

/** 정렬 값. date=일정순(착공일 오름차순) · urgent=급한순(대표색 심각도 후 착공일). 마지막 기준은 taskId 오름차순 */
export type ListSort = 'date' | 'urgent'

/** 정렬 버튼 문구 — 현재 상태를 보여 주고 누르면 반대로 바뀜 */
export const LIST_SORT_LABEL: Record<ListSort, string> = {
  date: '⇅ 일정순',
  urgent: '⇅ 급한순',
}

/** 대표색 심각도 순서(작을수록 급함). 신본 RK: 빨강 < 노랑 < 파랑 < 녹색 < 흰색 < 해당 없음 */
export const LIST_COLOR_RANK: Record<SignalColor, number> = {
  RED: 0, YELLOW: 1, BLUE: 2, GREEN: 3, WHITE: 4, NONE: 5,
}

/** 빈 상태 문구(필터 통과 0건) */
export const LIST_EMPTY_TEXT = '조건에 맞는 공종이 없어요'
